import { REFERRAL_CODE_QUERY_PARAM_NAME } from 'routes/constants';
import { useNavigate } from 'react-router';
import { useCallback } from 'react';
import { ChainID } from '@ankr.com/chains-list';

import { createRouteConfig } from 'modules/router/utils/createRouteConfig';
import { useQueryParams } from 'modules/common/hooks/useQueryParams';

export const PROJECT_INDEX_QUERY = 'projectIndex';
// Legacy query param that contained the project API key.
// Kept read-only to redirect old links to the projectIndex-based URL.
export const LEGACY_PROJECT_ID_QUERY = 'projectId';

const CHAIN_ID_QUERY = 'chainId';

const parseProjectIndex = (value: string | null) => {
  if (value === null || value === '') {
    return undefined;
  }

  const projectIndex = Number(value);

  return Number.isInteger(projectIndex) && projectIndex >= 0
    ? projectIndex
    : undefined;
};

export const PROJECTS_PATH = '/projects/';

const NEW_PROJECT_PATH = `${PROJECTS_PATH}new/`;

export const projectsRoutesConfig = createRouteConfig(
  {
    projects: {
      path: PROJECTS_PATH,
      generatePath: () => {
        const { search } = window.location;
        const queryParams = new URLSearchParams(search);
        const referralCode = queryParams.get(REFERRAL_CODE_QUERY_PARAM_NAME);

        if (referralCode) {
          const utmSearch = new URLSearchParams({
            [REFERRAL_CODE_QUERY_PARAM_NAME]: referralCode,
          }).toString();

          return `${PROJECTS_PATH}?${utmSearch}`;
        }

        return PROJECTS_PATH;
      },
    },
    project: {
      path: PROJECTS_PATH,
      generatePath: (projectIndex: number) => {
        const params = new URLSearchParams({
          [PROJECT_INDEX_QUERY]: projectIndex.toString(),
        });

        return `${PROJECTS_PATH}?${params.toString()}`;
      },
      useParams: () => {
        const navigate = useNavigate();
        const query = useQueryParams();

        const projectIndex = parseProjectIndex(query.get(PROJECT_INDEX_QUERY));
        const legacyProjectId = query.get(LEGACY_PROJECT_ID_QUERY) || undefined;
        const chainId =
          (query.get(CHAIN_ID_QUERY) as ChainID | undefined) || undefined;

        const setChainId = useCallback(
          (id: ChainID) => {
            query.set(CHAIN_ID_QUERY, id);

            navigate({ search: query.toString() }, { replace: true });
          },
          [navigate, query],
        );

        return { chainId, legacyProjectId, projectIndex, setChainId };
      },
    },
    newProject: {
      path: NEW_PROJECT_PATH,
      generatePath: () => NEW_PROJECT_PATH,
    },
  },
  PROJECTS_PATH,
);
